import { useCallback, useState } from 'react';

import { type AppOption } from '../../../../../common/utils/app-filter-options';

export type Tile = { key: string; option: AppOption };

export type PresentTile = Tile & { isEntering: boolean; isLeaving: boolean };

type LeavingTile = { tile: Tile; index: number };

type PresenceState = {
	/** The tiles as of the last change in which tiles are shown; removed ones leave from here. */
	tiles: Tile[];
	keys: string;
	selection: string;
	leaving: LeavingTile[];
	entering: ReadonlySet<string>;
};

const NONE: ReadonlySet<string> = new Set();

/**
 * Keeps a tile that has just left the row rendered, under its own key, until its exit
 * animation finishes. React unmounting it straight away would leave nothing to animate,
 * so the row would jolt by a tile's width in a single frame.
 *
 * Only a selection change animates tiles in and out. A tile that moves to or from More
 * apps because the row's width changed (including the first measurement after mount)
 * snaps, so the row doesn't animate while the page loads or the window resizes.
 */
export const useTilePresence = (
	tiles: Tile[],
	selection: string,
): { presentTiles: PresentTile[]; onTileLeft: (key: string) => void } => {
	// Compared by key, not array identity: the options are rebuilt on most renders, and
	// only a change in which tiles are shown matters here.
	const keys = tiles.map(({ key }) => key).join('\n');
	const [state, setState] = useState<PresenceState>(() => ({
		tiles,
		keys,
		selection,
		leaving: [],
		entering: NONE,
	}));

	let current = state;

	// Derived during render rather than in an effect: a removed tile must still be in
	// this render's output, or React unmounts it before the exit can start.
	if (state.keys !== keys || state.selection !== selection) {
		const shouldAnimate = state.selection !== selection;
		const previousKeys = new Set(state.tiles.map(({ key }) => key));
		const nextKeys = new Set(tiles.map(({ key }) => key));

		const removed: LeavingTile[] = shouldAnimate
			? state.tiles.flatMap((tile, index) =>
					nextKeys.has(tile.key)
						? []
						: // It lost the selection too, so it deselects as it collapses.
							[{ tile: { ...tile, option: { ...tile.option, isSelected: false } }, index }],
				)
			: [];
		const removedKeys = new Set(removed.map(({ tile }) => tile.key));

		current = {
			tiles,
			keys,
			selection,
			leaving: [
				...state.leaving.filter(
					({ tile }) => !nextKeys.has(tile.key) && !removedKeys.has(tile.key),
				),
				...removed,
			],
			entering: shouldAnimate
				? new Set(tiles.filter(({ key }) => !previousKeys.has(key)).map(({ key }) => key))
				: NONE,
		};
		setState(current);
	}

	const onTileLeft = useCallback((key: string) => {
		setState((previous) => ({
			...previous,
			leaving: previous.leaving.filter(({ tile }) => tile.key !== key),
		}));
	}, []);

	const presentTiles: PresentTile[] = tiles.map((tile) => ({
		...tile,
		isEntering: current.entering.has(tile.key),
		isLeaving: false,
	}));

	// Back where it was, so it collapses in place rather than jumping to the end.
	[...current.leaving]
		.sort((a, b) => a.index - b.index)
		.forEach(({ tile, index }) => {
			presentTiles.splice(Math.min(index, presentTiles.length), 0, {
				...tile,
				isEntering: false,
				isLeaving: true,
			});
		});

	return { presentTiles, onTileLeft };
};
